import type { HTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AlertCircle,
  ExternalLink,
  LayoutGrid,
  ListFilter,
  Loader2,
  Search,
  Table2,
  X,
} from 'lucide-react';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export type ManagementViewMode = 'table' | 'cards';
export type ManagementTone = 'default' | 'success' | 'warning' | 'destructive' | 'info';

export interface ManagementSource {
  label: string;
  system?: string;
  href?: string;
}

export interface ManagementMetric {
  id: string;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: LucideIcon;
  tone?: ManagementTone;
  active?: boolean;
  onClick?: () => void;
}

export interface ManagementFilterOption {
  value: string;
  label: string;
  count?: number;
  title?: string;
}

export interface ManagementFilterGroup {
  id: string;
  label?: string;
  value?: string;
  options: ManagementFilterOption[];
  onChange: (value: string) => void;
  widthClassName?: string;
}

export interface ManagementActiveFilter {
  id: string;
  label: string;
  value?: string;
  onClear: () => void;
}

export interface ManagementSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

export interface ManagementAction {
  label: string;
  onClick?: () => void;
  href?: string;
  icon?: LucideIcon;
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  disabled?: boolean;
}

interface ManagementPageProps {
  children: ReactNode;
  className?: string;
}

export function ManagementPageShell({ children, className }: ManagementPageProps) {
  return (
    <div
      className={cn(
        'oneup-management-page mx-auto flex w-full max-w-[1480px] flex-col gap-3 sm:gap-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

export const ManagementPage = ManagementPageShell;

interface ManagementHeaderProps {
  title: string;
  description?: ReactNode;
  source?: ManagementSource;
  metrics?: ManagementMetric[];
  /**
   * Render the metric tile row. Defaults to false: list/index views rely on
   * search + dropdown filters instead, and the count tiles were redundant and
   * bulky (Pass 6 cleanup). Opt in (e.g. the UI kit / a genuine dashboard) by
   * passing showMetrics.
   */
  showMetrics?: boolean;
  metricGridClassName?: string;
  search?: ManagementSearchProps;
  filters?: ManagementFilterGroup[];
  activeFilters?: ManagementActiveFilter[];
  primaryAction?: ManagementAction;
  secondaryActions?: ManagementAction[];
  viewMode?: ManagementViewMode;
  onViewModeChange?: (mode: ManagementViewMode) => void;
}

export function ManagementHeader({
  title,
  description,
  source,
  metrics = [],
  showMetrics = false,
  metricGridClassName,
  search,
  filters = [],
  activeFilters = [],
  primaryAction,
  secondaryActions = [],
  viewMode,
  onViewModeChange,
}: ManagementHeaderProps) {
  const hasToolbar = Boolean(search || filters.length > 0 || activeFilters.length > 0);

  return (
    <section className="space-y-3">
      <ManagementPageHeader
        title={title}
        description={description}
        source={source}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
      />
      {showMetrics && metrics.length > 0 && (
        <ManagementMetricGrid metrics={metrics} className={metricGridClassName} />
      )}
      {hasToolbar && (
        <ManagementToolbar
          filters={filters}
          search={search}
          activeFilters={activeFilters}
        />
      )}
    </section>
  );
}

export function ManagementPageHeader({
  title,
  description,
  source,
  primaryAction,
  secondaryActions = [],
  viewMode,
  onViewModeChange,
}: {
  title: string;
  description?: ReactNode;
  source?: ManagementSource;
  primaryAction?: ManagementAction;
  secondaryActions?: ManagementAction[];
  viewMode?: ManagementViewMode;
  onViewModeChange?: (mode: ManagementViewMode) => void;
}) {
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
          <h2 className="text-[22px] font-semibold leading-tight text-foreground">{title}</h2>
          {source && <ManagementSourceBadge source={source} />}
        </div>
        {description && (
          <p className="max-w-3xl text-[13px] leading-5 text-muted-foreground">{description}</p>
        )}
      </div>
      <ManagementActionGroup>
        {source?.href && (
          <ManagementActionButton
            action={{ label: 'Open source', href: source.href, icon: ExternalLink }}
          />
        )}
        {secondaryActions.map((action) => (
          <ManagementActionButton key={action.label} action={action} />
        ))}
        {viewMode && onViewModeChange && (
          <ManagementViewToggle value={viewMode} onChange={onViewModeChange} />
        )}
        {primaryAction && <ManagementActionButton action={primaryAction} primary />}
      </ManagementActionGroup>
    </div>
  );
}

export function ManagementSourceBadge({ source }: { source: ManagementSource }) {
  return (
    <span className="inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border border-border/70 bg-card px-2.5 text-[11px] font-medium text-muted-foreground shadow-[0_1px_1px_hsl(var(--foreground)/0.03)]">
      <span className="h-1.5 w-1.5 rounded-full bg-primary/65" aria-hidden />
      <span>{source.system ?? 'Source'}</span>
      <span className="text-muted-foreground/45">/</span>
      <span className="max-w-[230px] truncate text-foreground">{source.label}</span>
    </span>
  );
}

export function ManagementActionGroup({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5 lg:justify-end', className)}>
      {children}
    </div>
  );
}

export function ManagementPrimaryAction({ action }: { action: ManagementAction }) {
  return <ManagementActionButton action={action} primary />;
}

export function ManagementActionButton({
  action,
  primary = false,
}: {
  action: ManagementAction;
  primary?: boolean;
}) {
  const Icon = action.icon;
  const variant = primary ? 'default' : action.variant ?? 'outline';
  const className = cn(
    'h-8 gap-1.5 rounded-md px-2.5 text-[12px] shadow-none',
    primary && 'px-3.5 shadow-[0_1px_2px_hsl(var(--primary)/0.2)]',
  );

  if (action.href) {
    return (
      <Button asChild size="sm" variant={variant} className={className}>
        <a href={action.href} target="_blank" rel="noreferrer">
          {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
          {action.label}
        </a>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant={variant}
      className={className}
      disabled={action.disabled}
      onClick={action.onClick}
    >
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
      {action.label}
    </Button>
  );
}

export function ManagementViewToggle({
  value,
  onChange,
}: {
  value: ManagementViewMode;
  onChange: (mode: ManagementViewMode) => void;
}) {
  const options: Array<{ value: ManagementViewMode; label: string; icon: LucideIcon }> = [
    { value: 'table', label: 'Table', icon: Table2 },
    { value: 'cards', label: 'Cards', icon: LayoutGrid },
  ];

  return (
    <div className="inline-flex h-8 rounded-md border border-border/70 bg-muted/45 p-0.5">
      {options.map((option) => {
        const Icon = option.icon;
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={active}
            className={cn(
              'inline-flex min-w-8 items-center justify-center gap-1.5 rounded-[5px] px-2 text-[12px] font-medium transition-colors',
              active
                ? 'bg-card text-foreground shadow-[0_1px_2px_hsl(var(--foreground)/0.08)]'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            <span className="hidden sm:inline">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function ManagementMetricGrid({
  metrics,
  children,
  className,
}: {
  metrics?: ManagementMetric[];
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('grid gap-2 sm:grid-cols-2 xl:grid-cols-4', className)}>
      {metrics?.map((metric) => (
        <ManagementMetricCard key={metric.id} metric={metric} />
      ))}
      {children}
    </div>
  );
}

export function ManagementMetricCard({ metric }: { metric: ManagementMetric }) {
  const Icon = metric.icon;
  const content = (
    <>
      <div className="flex items-center justify-between gap-3">
        <span className="truncate text-[10px] font-semibold uppercase tracking-[0.55px] text-muted-foreground">
          {metric.label}
        </span>
        {Icon && (
          <span
            className={cn(
              'inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground',
              metric.tone === 'success' && 'bg-success/10 text-success',
              metric.tone === 'warning' && 'bg-warning/10 text-warning',
              metric.tone === 'destructive' && 'bg-destructive/10 text-destructive',
              metric.tone === 'info' && 'bg-info/10 text-info',
              metric.active && 'bg-primary/10 text-primary',
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
          </span>
        )}
      </div>
      <div
        className={cn(
          'mt-2 text-[25px] font-semibold leading-none tabular-nums text-foreground',
          metric.tone === 'success' && 'text-success',
          metric.tone === 'warning' && 'text-warning',
          metric.tone === 'destructive' && 'text-destructive',
          metric.tone === 'info' && 'text-info',
        )}
      >
        {metric.value}
      </div>
      {metric.hint && (
        <div className="mt-1.5 truncate text-[11px] leading-4 text-muted-foreground">
          {metric.hint}
        </div>
      )}
    </>
  );

  const className = cn(
    'relative min-h-[86px] min-w-0 overflow-hidden rounded-lg border border-border/70 bg-card px-3.5 py-3 text-left shadow-[0_1px_2px_hsl(var(--foreground)/0.04)] transition-colors',
    metric.onClick && 'cursor-pointer hover:border-foreground/15 hover:bg-muted/20',
    metric.active && 'bg-primary/[0.025] shadow-[inset_0_2px_0_hsl(var(--primary)),0_1px_2px_hsl(var(--foreground)/0.04)]',
  );

  if (!metric.onClick) {
    return <div className={className}>{content}</div>;
  }

  return (
    <button type="button" onClick={metric.onClick} className={className}>
      {content}
    </button>
  );
}

export function ManagementToolbar({
  filters = [],
  search,
  activeFilters = [],
  children,
  className,
}: {
  filters?: ManagementFilterGroup[];
  search?: ManagementSearchProps;
  activeFilters?: ManagementActiveFilter[];
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
        {search && <ManagementSearch search={search} />}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {filters.map((group) => (
            <ManagementFilterSelect key={group.id} group={group} />
          ))}
          {children}
        </div>
      </div>
      {activeFilters.length > 0 && <ManagementActiveFilters filters={activeFilters} />}
    </div>
  );
}

export function ManagementSearch({ search }: { search: ManagementSearchProps }) {
  return (
    <label className="relative min-w-0 flex-1 lg:max-w-[460px]">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        type="search"
        value={search.value}
        onChange={(event) => search.onChange(event.target.value)}
        placeholder={search.placeholder}
        className="h-8 rounded-md border-border/80 bg-background/80 pl-8 text-[13px] shadow-none"
      />
    </label>
  );
}

export function ManagementFilterSelect({ group }: { group: ManagementFilterGroup }) {
  return (
    <label
      className={cn(
        'inline-flex h-8 min-w-0 items-center gap-2 rounded-md border border-border/70 bg-card/70 px-2 text-[12px] shadow-none transition-colors focus-within:border-primary/45 focus-within:ring-1 focus-within:ring-ring/35',
        group.widthClassName ?? 'w-full sm:w-[210px]',
      )}
    >
      <span className="inline-flex shrink-0 items-center gap-1.5 font-semibold text-muted-foreground">
        <ListFilter className="h-3.5 w-3.5" aria-hidden />
        {group.label}
      </span>
      <select
        value={group.value}
        onChange={(event) => group.onChange(event.currentTarget.value)}
        className="h-full min-w-0 flex-1 appearance-none bg-transparent text-[12px] font-medium text-foreground outline-none"
        aria-label={group.label ?? group.id}
      >
        {group.options.map((option) => (
          <option key={option.value} value={option.value} title={option.title}>
            {option.label}
            {option.count !== undefined ? ` (${option.count})` : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ManagementFilterChips({ group }: { group: ManagementFilterGroup }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {group.label && (
        <span className="text-[11px] font-medium text-muted-foreground">{group.label}</span>
      )}
      {group.options.map((option) => (
        <ManagementFilterChip
          key={option.value}
          option={option}
          active={option.value === group.value}
          onClick={() => group.onChange(option.value)}
        />
      ))}
    </div>
  );
}

export function ManagementFilterChip({
  option,
  active,
  onClick,
}: {
  option: ManagementFilterOption;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={option.title}
      aria-pressed={active}
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[12px] font-medium transition-colors',
        active
          ? 'border-primary/35 bg-primary/[0.08] text-primary'
          : 'border-border/70 bg-background/70 text-muted-foreground hover:border-foreground/15 hover:bg-muted/35 hover:text-foreground',
      )}
    >
      {option.label}
      {option.count !== undefined && (
        <span
          className={cn(
            'rounded-full px-1.5 text-[10px] tabular-nums',
            active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
          )}
        >
          {option.count}
        </span>
      )}
    </button>
  );
}

export function ManagementActiveFilters({ filters }: { filters: ManagementActiveFilter[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
      <span className="font-medium text-muted-foreground">Active</span>
      {filters.map((filter) => (
        <button
          key={filter.id}
          type="button"
          onClick={filter.onClear}
          className="inline-flex h-7 items-center gap-1 rounded-full border border-primary/20 bg-primary/[0.055] px-2 text-[11px] font-medium text-foreground hover:border-primary/40 hover:text-primary"
        >
          <span>{filter.label}</span>
          {filter.value && <span className="text-muted-foreground">{filter.value}</span>}
          <X className="h-3 w-3" aria-hidden />
        </button>
      ))}
    </div>
  );
}

export function ManagementBulkActionBar({
  selectedCount,
  children,
  onClear,
}: {
  selectedCount: number;
  children: ReactNode;
  onClear?: () => void;
}) {
  if (selectedCount <= 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/20 bg-card px-3 py-2 shadow-[0_1px_2px_hsl(var(--foreground)/0.04)]">
      <div className="flex items-center gap-2 text-[12px] font-medium text-foreground">
        <Badge variant="default" className="h-6 px-2">
          {selectedCount} selected
        </Badge>
        <span className="text-muted-foreground">Bulk actions</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {children}
        {onClear && (
          <Button type="button" size="sm" variant="ghost" className="h-8 px-2.5" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}

export const ManagementBulkBar = ManagementBulkActionBar;

export function ManagementTableSurface({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'overflow-x-auto rounded-lg border border-border/70 bg-card shadow-[0_1px_2px_hsl(var(--foreground)/0.04)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function ManagementTable({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <ManagementTableSurface
      className={cn(
        '[&_table]:w-full [&_table]:text-left [&_table]:text-[13px]',
        '[&_thead_tr]:border-b [&_thead_tr]:border-border/70 [&_thead_tr]:bg-muted/35',
        '[&_th]:h-10 [&_th]:whitespace-nowrap [&_th]:px-3.5 [&_th]:text-[10.5px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-[0.55px] [&_th]:text-muted-foreground',
        '[&_tbody_tr]:border-b [&_tbody_tr]:border-border/55 [&_tbody_tr]:transition-colors [&_tbody_tr:last-child]:border-b-0 [&_tbody_tr:hover]:bg-muted/25',
        '[&_td]:px-3.5 [&_td]:py-3',
        className,
      )}
    >
      <div className="overflow-x-auto">{children}</div>
    </ManagementTableSurface>
  );
}

export const ManagementDataTable = ManagementTable;

export function ManagementCardGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('grid gap-2.5 md:grid-cols-2 xl:grid-cols-3', className)}>
      {children}
    </div>
  );
}

export function ManagementRecordCard({
  children,
  className,
  active,
  selected,
  ...props
}: HTMLAttributes<HTMLDivElement> & { active?: boolean; selected?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-lg border border-border/70 bg-card p-3 shadow-[0_1px_2px_hsl(var(--foreground)/0.04)] transition-colors hover:border-foreground/15 hover:bg-muted/20',
        selected && 'border-primary/20 bg-primary/[0.025]',
        active && 'border-primary/35 bg-primary/[0.045] shadow-[inset_0_2px_0_hsl(var(--primary)),0_1px_2px_hsl(var(--foreground)/0.04)]',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function ManagementStatusBadge({
  children,
  variant = 'muted',
  className,
}: {
  children: ReactNode;
  variant?: BadgeProps['variant'];
  className?: string;
}) {
  return (
    <Badge variant={variant} className={cn('h-6 rounded-full px-2 text-[11px]', className)}>
      {children}
    </Badge>
  );
}

export function ManagementPriorityBadge({
  children,
  variant = 'muted',
  className,
}: {
  children: ReactNode;
  variant?: BadgeProps['variant'];
  className?: string;
}) {
  return (
    <Badge variant={variant} className={cn('h-6 rounded-full px-2 text-[11px]', className)}>
      {children}
    </Badge>
  );
}

export function ManagementFlagBadge({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'h-6 rounded-full border-border/70 bg-background/70 px-2 text-[11px] text-muted-foreground',
        className,
      )}
    >
      {children}
    </Badge>
  );
}

export function ManagementFooterSummary({ children }: { children: ReactNode }) {
  return <div className="px-0.5 text-[11px] text-muted-foreground">{children}</div>;
}

export function ManagementLoadingState({ metricCount = 4 }: { metricCount?: number }) {
  return (
    <ManagementPage>
      <section className="space-y-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="h-6 w-44 animate-pulse rounded bg-muted" />
            <div className="mt-2 h-3 w-96 max-w-full animate-pulse rounded bg-muted" />
          </div>
          <div className="h-8 w-56 animate-pulse rounded-md bg-muted" />
        </div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: metricCount }).map((_, index) => (
            <div key={index} className="h-[86px] animate-pulse rounded-lg border border-border/70 bg-card" />
          ))}
        </div>
        <div className="h-14 animate-pulse rounded-lg border border-border/70 bg-card" />
      </section>
      <div className="flex h-[420px] items-center justify-center rounded-lg border border-border/70 bg-card">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
      </div>
    </ManagementPage>
  );
}

export function ManagementErrorState({ title, error }: { title: string; error: unknown }) {
  return (
    <div className="rounded-lg border border-destructive/20 bg-card p-5 shadow-[0_1px_2px_hsl(var(--foreground)/0.04)]">
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" aria-hidden />
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <p className="mt-1 text-[13px] text-muted-foreground">
        {error instanceof Error ? error.message : 'Unknown error'}
      </p>
    </div>
  );
}

export function ManagementEmptyState({
  icon: Icon,
  title,
  body,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-12 text-center text-sm text-muted-foreground">
      {Icon && (
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-muted/55 text-muted-foreground">
          <Icon className="h-4 w-4" aria-hidden />
        </span>
      )}
      <div>
        <p className="font-medium text-foreground">{title}</p>
        {body && <div className="mt-1 max-w-md text-[12px] leading-relaxed">{body}</div>}
      </div>
      {action}
    </div>
  );
}
